/**
 * Gamify SDK Bridge
 * 
 * Handles communication between the visualizer iframe and the parent
 * Gamify SDK via postMessage. Implements the message protocol defined
 * in the Gamify Toolkit documentation.
 */

import type {
  GamifyVisualizerPayload,
  VisualizerConfig,
  VisualizerTheme,
  VisualizerSettings,
  CustomizationState,
  VisualizerEvent,
  VisualizerEventType,
  InboundMessage,
  OutboundMessage,
} from '../types';

export interface GamifySdkBridgeOptions {
  /** Timeout for waiting for config (ms) */
  initTimeout?: number;
  
  /** Enable debug logging */
  debug?: boolean;
  
  /** Allowed origin for postMessage (default: '*') */
  allowedOrigin?: string;
}

type MessageHandler<T> = (payload: T) => void;

export class GamifySdkBridge {
  private config: VisualizerConfig | null = null;
  private theme: VisualizerTheme | null = null;
  private settings: VisualizerSettings | null = null;
  private session: { sessionId: string; token: string } | null = null;
  
  private configId: string | null = null;
  private shop: string | null = null;
  
  private isReady = false;
  private debug: boolean;
  private allowedOrigin: string;
  private initTimeout: number;
  
  private messageHandlers = new Map<string, MessageHandler<unknown>[]>();
  private boundMessageHandler: (event: MessageEvent) => void;
  
  constructor(options: GamifySdkBridgeOptions = {}) {
    this.debug = options.debug ?? false;
    this.allowedOrigin = options.allowedOrigin ?? '*';
    this.initTimeout = options.initTimeout ?? 10000;
    
    this.boundMessageHandler = this.handleMessage.bind(this);
  }
  
  /**
   * Initialize the bridge and wait for config from parent
   */
  public init(): Promise<GamifyVisualizerPayload> {
    return new Promise((resolve, reject) => {
      // Set up message listener
      window.addEventListener('message', this.boundMessageHandler);
      
      // Set up timeout
      const timeoutId = setTimeout(() => {
        reject(new Error(`SDK init timeout: No config received within ${this.initTimeout}ms`));
      }, this.initTimeout);
      
      // Register one-time handler for config
      this.once('gamify:config', (payload: GamifyVisualizerPayload) => {
        clearTimeout(timeoutId);
        
        this.configId = payload.configId;
        this.shop = payload.shop;
        this.config = payload.config;
        this.theme = payload.theme;
        this.settings = payload.settings;
        this.session = payload.session ?? null;
        this.isReady = true;
        
        this.log('Config received:', payload);
        resolve(payload);
      });
      
      // Signal to parent that visualizer is ready
      this.postMessage({ type: 'gamify:visualizer-ready' });
      this.log('Sent visualizer-ready signal');
    });
  }
  
  /**
   * Handle incoming postMessage
   */
  private handleMessage(event: MessageEvent): void {
    // Origin validation (if specified)
    if (this.allowedOrigin !== '*' && event.origin !== this.allowedOrigin) {
      this.log('Ignored message from unauthorized origin:', event.origin);
      return;
    }
    
    const message = event.data as InboundMessage;
    if (!message || typeof message.type !== 'string') {
      return; // Not a valid message
    }
    
    this.log('Received message:', message.type, message);
    
    // Dispatch to handlers
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      for (const handler of handlers) {
        handler((message as { payload?: unknown }).payload);
      }
    }
    
    // Handle built-in message types
    switch (message.type) {
      case 'gamify:update-theme':
        this.theme = (message as { payload: VisualizerTheme }).payload;
        break;
      case 'gamify:update-settings':
        if (this.settings) {
          this.settings = { ...this.settings, ...(message as { payload: Partial<VisualizerSettings> }).payload };
        }
        break;
    }
  }
  
  /**
   * Post message to parent window
   */
  private postMessage(message: OutboundMessage): void {
    if (window.parent !== window) {
      window.parent.postMessage(message, this.allowedOrigin);
      this.log('Sent message:', message.type, message);
    }
  }
  
  /**
   * Record a visualizer event
   */
  public recordEvent<T = unknown>(type: VisualizerEventType, data: T): void {
    const event: VisualizerEvent<T> = {
      type,
      timestamp: Date.now(),
      data,
    };
    
    this.postMessage({
      type: 'gamify:event',
      payload: event,
    });
  }
  
  /**
   * Save the current customization state
   */
  public saveCustomization(state: CustomizationState): void {
    this.postMessage({
      type: 'gamify:save-customization',
      payload: state,
    });
  }
  
  /**
   * Send a screenshot to parent
   */
  public sendScreenshot(dataUrl: string): void {
    this.postMessage({
      type: 'gamify:screenshot',
      payload: { dataUrl },
    });
  }
  
  /**
   * Report an error to parent
   */
  public reportError(code: string, message: string): void {
    this.postMessage({
      type: 'gamify:error',
      payload: { code, message },
    });
  }
  
  /**
   * Subscribe to a message type
   */
  public on<T = unknown>(messageType: string, handler: MessageHandler<T>): void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType)!.push(handler as MessageHandler<unknown>);
  }
  
  /**
   * Subscribe to a message type (one-time)
   */
  public once<T = unknown>(messageType: string, handler: MessageHandler<T>): void {
    const wrappedHandler = (payload: T) => {
      this.off(messageType, wrappedHandler as MessageHandler<unknown>);
      handler(payload);
    };
    this.on(messageType, wrappedHandler);
  }
  
  /**
   * Unsubscribe from a message type
   */
  public off(messageType: string, handler: MessageHandler<unknown>): void {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }
  
  /**
   * Get the visualizer config
   */
  public getConfig(): VisualizerConfig | null {
    return this.config;
  }
  
  /**
   * Get the theme
   */
  public getTheme(): VisualizerTheme | null {
    return this.theme;
  }
  
  /**
   * Get the settings
   */
  public getSettings(): VisualizerSettings | null {
    return this.settings;
  }
  
  /**
   * Get the session info
   */
  public getSession(): { sessionId: string; token: string } | null {
    return this.session;
  }
  
  /**
   * Get the shop domain
   */
  public getShop(): string | null {
    return this.shop;
  }
  
  /**
   * Get the config ID
   */
  public getConfigId(): string | null {
    return this.configId;
  }
  
  /**
   * Check if bridge is ready
   */
  public getIsReady(): boolean {
    return this.isReady;
  }
  
  /**
   * Debug logging
   */
  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[GamifySdkBridge]', ...args);
    }
  }
  
  /**
   * Clean up
   */
  public dispose(): void {
    window.removeEventListener('message', this.boundMessageHandler);
    this.messageHandlers.clear();
    this.isReady = false;
  }
}
